/**
 * core/persistence-sentinels.ts — post-restore inflight Map seeding.
 *
 * SPEC ANCHOR: file 05 §persistence-sentinels.
 *
 * THE PROBLEM
 * -----------
 * The module-level inflight Map (`core/inflight.ts`) resets across
 * reload. A persisted-and-resumed paused mutation never re-runs
 * `onMutate` (its snapshot was captured pre-pause), so it never inserts
 * into the inflight Map. A fresh same-scope `mutate()` during drain
 * finds the gate empty and races.
 *
 * THE FIX
 * -------
 * On SDKProvider mount, AFTER `persistQueryClientRestore` resolves but
 * BEFORE `resumePausedMutations`:
 *
 *   1. Walk `qc.getMutationCache().getAll()` and filter
 *      `m.state.isPaused === true`.
 *   2. For each, derive `scopeKey` from `m.options.scope?.id`.
 *      (No scope.id → skip; the mutation cannot collide with anything.)
 *   3. Install a sentinel Promise into the inflight Map that resolves
 *      when the mutation settles (success OR error).
 *   4. Subscribe to the mutation cache; on the matching mutation's
 *      settle, resolve the sentinel, evict the Map entry by
 *      compare-by-reference, and unsubscribe.
 *
 * Mandatory call ORDER:
 *   1. `new QueryClient()`
 *   2. `setMutationDefaults(...)` for every codegen'd spec
 *   3. `persistQueryClientRestore(...)`
 *   4. `installPersistenceSentinels(qc)` ← this function
 *   5. `qc.resumePausedMutations()`
 */

import type { QueryClient } from '@tanstack/react-query';
import { getInflightFor } from './inflight.js';

/**
 * Walks paused mutations and installs sentinel Promises in the inflight
 * Map so that a fresh same-scope `mutate()` awaits the sentinel rather
 * than racing the resumed paused mutation.
 *
 * Returns nothing; side effects are confined to the inflight Map and a
 * single `getMutationCache().subscribe()` per paused mutation. Each
 * subscription auto-unsubscribes when its mutation settles, so there is
 * no long-lived subscription leak.
 */
export function installPersistenceSentinels(qc: QueryClient): void {
  const inflight = getInflightFor(qc);
  const paused = qc
    .getMutationCache()
    .getAll()
    .filter((m) => m.state.isPaused);
  for (const m of paused) {
    const scopeKey = m.options.scope?.id;
    if (!scopeKey) continue;
    let resolveTail!: () => void;
    const tail = new Promise<void>((resolve) => {
      resolveTail = resolve;
    });
    inflight.set(scopeKey, tail);

    const unsubscribe = qc.getMutationCache().subscribe((event) => {
      if (event.mutation !== m) return;
      const status = m.state.status;
      if (status !== 'success' && status !== 'error') return;
      resolveTail();
      // Compare-by-reference cleanup — protects against successor tails
      // being clobbered if cleanup fires out of order.
      if (inflight.get(scopeKey) === tail) inflight.delete(scopeKey);
      unsubscribe();
    });
  }
}
